<template>
  <div>
    <div class="row gx-0" :class="rowClass">
      <!-- image -->
      <div
        ref="carouselImageCover"
        class="col-12 col-xl-7 bg-white cia-bg-image position-relative">
        <img
          v-for="(img, index) in items"
          :key="index"
          :src="img.imageSource"
          :alt="img.title"
          class="cia-image"
          :style="transformTranslateImage" />
        <!-- gradient -->
        <div class="cia-bg-gradient" :class="gradientClass"></div>
      </div>
      <div class="col-12 col-xl-5">
        <div
          class="cia-card bg-dark-3 px-space-xs py-space-0 px-xl-space-md d-flex overflow-auto"
          :class="cardClasses">
          <div
            class="p-space-sm p-xl-space-md mb-space-xs mb-xl-0 bg-white d-flex flex-column w-100">
            <div
              class="mb-space-sm mb-xl-0 overflow-auto position-relative h-100 d-flex flex-column">
              <!-- indicators -->
              <div class="mb-space-sm mb-xl-space-md">
                <button
                  class="carousel-indicator me-space-xs"
                  :class="getCarouselClass(index)"
                  v-for="(item, index) in items"
                  :key="index"
                  @click="handleIndicatorClick(index)"
                  aria-label="carousel indicator"></button>
              </div>
              <div class="overflow-auto mb-space-sm" :key="currentSlideIndex">
                <!-- text -->
                <component :is="titleLevel" :class="titleClass">
                  {{ items[currentSlideIndex].title }}
                </component>
                <div
                  class="fs-small"
                  v-html="items[currentSlideIndex].text"
                  v-if="items[currentSlideIndex].text"></div>
                <!-- optional slide content using dynamic slot names -->
                <slot :name="`slide(${currentSlideIndex + 1})`"> </slot>
                <!-- optional cta -->
              </div>
            </div>
            <!-- carousel controls -->
            <div
              class="carousel-buttons flex-grow-1 d-flex justify-content-end align-items-end flex-wrap">
              <div
                v-if="items[currentSlideIndex].displayCta"
                class="mt-space-sm flex-grow-1 mb-space-sm mb-sm-0 d-flex">
                <a
                  @click="handleCtaClick"
                  :href="items[currentSlideIndex].ctaLink"
                  :target="items[currentSlideIndex].ctaLinkTarget"
                  class="btn"
                  :class="ctaClass"
                  >{{ items[currentSlideIndex].ctaText }}</a
                >
              </div>
              <div class="d-flex justify-content-end">
                <button
                  class="carousel-control me-space-xs"
                  :class="{ disable: currentSlideIndex === 0 }"
                  :disabled="currentSlideIndex === 0"
                  @click="previousCarouselSlide">
                  <img alt="left-chevron" :src="leftArrow" />
                </button>
                <button
                  class="carousel-control d-flex justify-content-center align-items-center"
                  :class="{
                    disable: currentSlideIndex === items.length - 1,
                  }"
                  :disabled="currentSlideIndex === items.length - 1"
                  @click="nextCarouselSlide">
                  <img alt="right-chevron" :src="rightArrow" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import leftArrow from "./assets/left.svg?url";
import rightArrow from "./assets/right.svg?url";

export interface CarouselItem {
  imageSource: string;
  title: string;
  text?: string;
  displayCta?: boolean;
  ctaLink?: string;
  ctaLinkTarget?: string;
  ctaText?: string;
}

export interface Props {
  contentSide?: "left" | "right";
  ctaTextSize?: "small" | "medium" | "large" | "xl";
  ctaVariant?: string;
  items: CarouselItem[];
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
  indicatorVariant?: string;
  activeIndicatorVariant?: string;
  cardPaddingY?:
    | "xxxs"
    | "xxs"
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "xxl"
    | "xxxl";
}

const props = withDefaults(defineProps<Props>(), {
  contentSide: "right",
  ctaTextSize: "small",
  ctaVariant: "dark-3",
  titleLevel: "h3",
  titleSize: "large",
  titleVariant: "dark-3",
  indicatorVariant: "light-4",
  activeIndicatorVariant: "primary",
  cardPaddingY: "xxl",
});

const emits = defineEmits<{
  /**
   *  emitted when next button clicked.
   */
  (e: "nextClicked"): void;
  /**
   *  emitted when previous button clicked.
   */
  (e: "previousClicked"): void;
  /**
   *  emitted when cta clicked.
   */
  (e: "ctaClick", event: Event): void;
  /**
   * emitted when a current slide indicator is clicked. passes up the index of the slide that corresponds to the indicator.
   */
  (e: "indicatorClick", slideIndex: number): void;
}>();

const currentSlideIndex = ref(0);
const translateImage = ref(0);
const carouselImageCover = ref<HTMLElement | null>();

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} mb-space-sm fw-bold text-${props.titleVariant}`;
});
const ctaClass = computed(() => {
  return `fs-${props.ctaTextSize} btn-${props.ctaVariant}`;
});
const transformTranslateImage = computed(() => {
  return `transform: translateX(${translateImage.value}px)`;
});
const rowClass = computed(() => {
  return props.contentSide == "left" ? "flex-row-reverse" : "";
});
const gradientClass = computed(() => {
  return props.contentSide == "left"
    ? " cia-bg-gradient-left"
    : " cia-bg-gradient-right";
});
const cardClasses = computed(() => {
  return ` py-xl-space-${props.cardPaddingY} `;
});

const getCarouselClass = (index: number) => {
  return currentSlideIndex.value === index
    ? ` bg-${props.activeIndicatorVariant} `
    : ` bg-${props.indicatorVariant} `;
};

const updateCarouselImage = () => {
  let childNode: HTMLElement | null;
  if (carouselImageCover.value?.childNodes[0].nodeName == "img") {
    childNode = carouselImageCover.value?.childNodes[0] as HTMLElement;
  } else {
    childNode = carouselImageCover.value?.childNodes[1] as HTMLElement;
  }
  translateImage.value = currentSlideIndex.value * childNode.clientWidth * -1;
};

const updateCarouselSlide = (slideNumber: number) => {
  currentSlideIndex.value = slideNumber;
  updateCarouselImage();
};

const nextCarouselSlide = () => {
  emits("nextClicked");
  let slideNumber = currentSlideIndex.value + 1;
  if (slideNumber >= props.items.length) {
    slideNumber = props.items.length - 1;
  }
  updateCarouselSlide(slideNumber);
};

const previousCarouselSlide = () => {
  emits("previousClicked");
  let slideNumber = currentSlideIndex.value - 1;
  if (slideNumber < 1) {
    slideNumber = 0;
  }
  updateCarouselSlide(slideNumber);
};

const handleIndicatorClick = (index: number) => {
  updateCarouselSlide(index);
  emits("indicatorClick", index);
};

const handleCtaClick = (event: Event) => {
  emits("ctaClick", event);
};

const handleResize = () => {
  updateCarouselImage();
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    window.addEventListener("resize", handleResize);
    handleResize();
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style lang="scss" scoped>
.carousel-control {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid var(--rds-light-4, #d0d0d0);
  background: var(--rds-light-3, #e8e8e8);
  cursor: pointer;
}
.disable {
  opacity: 0.5;
  cursor: auto;
}
.cia-bg-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
  height: auto;
  overflow: hidden;
  display: flex;
  position: relative;
  img {
    height: auto;
    min-width: 100%;
    transition: 0.3s all;
  }
}
.cia-bg-gradient {
  background: transparent
    linear-gradient(0deg, var(--rds-dark-3, #191919) 0%, #19191900 100%) 0% 0%
    no-repeat padding-box;
  width: 100%;
  min-height: 190px;
  bottom: 0px;
  position: absolute;
  right: 0;
}
.carousel-indicator {
  height: 8px;
  width: 32px;
  display: inline-block;
  cursor: pointer;

  border: 0;
  &:focus {
    outline: 1px solid var(--rds-dark-3, #191919);
  }
}
.cia-image {
  object-fit: cover;
  object-position: top left;
}
/* Large devices (desktops, 992px and up) */
@media (min-width: 1280px) {
  .cia-bg-gradient-right {
    background: transparent
      linear-gradient(270deg, var(--rds-dark-3, #191919) 0%, #19191900 100%) 0%
      0% no-repeat padding-box;
    width: 190px;
    height: 100%;
    position: absolute;
    margin-left: auto;
  }

  .cia-bg-gradient-left {
    background: transparent
      linear-gradient(90deg, var(--rds-dark-3, #191919) 0%, #19191900 100%) 0%
      0% no-repeat padding-box;
    width: 190px;
    height: 100%;
    position: absolute;
    left: 0;
  }

  .cia-card,
  .cia-bg-image {
    height: 618px;
  }
}
</style>
